import { z } from 'zod';

const nameSchema = z.string().trim().min(1, 'Укажи имя').max(48, 'Имя — до 48 символов')
  .refine((name) => !/[\p{Cc}\p{Cf}]/u.test(name), 'Имя должно быть одной строкой');

export const createDraftRoomSchema = z.object({
  title: nameSchema,
  names: z.array(nameSchema).min(4, 'Нужно минимум 4 игрока').max(24, 'Максимум 24 игрока'),
  captainA: z.number().int().nonnegative(),
  captainB: z.number().int().nonnegative(),
}).superRefine((data, ctx) => {
  if (new Set(data.names.map((name) => name.toLocaleLowerCase('ru'))).size !== data.names.length) {
    ctx.addIssue({ code: 'custom', path: ['names'], message: 'Имена повторяются. Добавь фамилию или прозвище' });
  }
  if (data.captainA === data.captainB || data.captainA >= data.names.length || data.captainB >= data.names.length) {
    ctx.addIssue({ code: 'custom', path: ['captainB'], message: 'Выбери двух разных капитанов из списка' });
  }
});

export const draftRoomParamsSchema = z.object({ id: z.string().uuid() });
export const draftPickSchema = z.object({ player: z.number().int().min(0).max(23), revision: z.number().int().nonnegative() });
export const draftActionSchema = z.object({});
export const draftBindCodeSchema = z.string().regex(/^[a-f0-9]{24}$/i);
export type CreateDraftRoomInput = z.infer<typeof createDraftRoomSchema>;
export const createProfileDraftRoomSchema = z.object({
  title: nameSchema,
  playerIds: z.array(z.string().uuid()).min(4, 'Нужно минимум 4 игрока').max(24, 'Максимум 24 игрока'),
  captainA: z.string().uuid('Выбери капитана A'),
  captainB: z.string().uuid('Выбери капитана B'),
}).superRefine((data, ctx) => {
  if (new Set(data.playerIds).size !== data.playerIds.length) ctx.addIssue({ code: 'custom', path: ['playerIds'], message: 'Игрок уже добавлен в состав' });
  if (data.captainA === data.captainB || !data.playerIds.includes(data.captainA) || !data.playerIds.includes(data.captainB)) {
    ctx.addIssue({ code: 'custom', path: ['captainB'], message: 'Выбери двух разных капитанов из состава' });
  }
});
export const draftProfileSearchSchema = z.object({ q: z.string().trim().min(2).max(80) });
export type CreateProfileDraftRoomInput = z.infer<typeof createProfileDraftRoomSchema>;
export interface DraftPlayerProfile {
  id: string;
  firstName: string;
  lastName: string | null;
  username: string;
  avatarUrl: string | null;
}
export function draftProfileName(profile: Pick<DraftPlayerProfile, 'firstName' | 'lastName' | 'username'>): string {
  return [profile.firstName?.trim(), profile.lastName?.trim()].filter(Boolean).join(' ') || `@${profile.username}`;
}
export type DraftTeam = 'A' | 'B';
export type DraftRole = DraftTeam | 'owner' | 'spectator';
export interface DraftLottery {
  rounds: { A: number; B: number }[];
  winner: DraftTeam;
}
export interface DraftState {
  /** Absent in rooms started before the first-pick lottery was introduced. */
  lottery?: DraftLottery;
  names: string[];
  /** Server-resolved profile snapshots. Absent only in older manual rooms. */
  players?: DraftPlayerProfile[];
  captains: { A: number; B: number };
  teams: { A: number[]; B: number[] };
  picks: { team: DraftTeam; player: number }[];
  status: 'ready' | 'drafting' | 'completed' | 'cancelled';
  turn: DraftTeam | null;
  revision: number;
}
export interface DraftRoomView {
  activityId?: string;
  id: string;
  title: string;
  state: DraftState;
  role: DraftRole;
  expiresAt: string;
  telegram: { available: boolean; connected: boolean; title: string | null; pending: number; error: boolean };
  access?: { captainA: string; captainB: string };
}
export interface DraftRoomCreated { room: DraftRoomView; ownerToken: string }
export interface DraftTelegramCode { code: string; expiresAt: string; botUsername: string }

export function draftPollingInterval(status: DraftState['status'] | undefined, pending = 0, errorCode?: string): number | false {
  if (errorCode && ['NOT_FOUND', 'EXPIRED', 'INVALID_KEY', 'VALIDATION_ERROR'].includes(errorCode)) return false;
  if (status === 'ready' || status === 'drafting' || status === undefined) return 2000;
  return pending > 0 ? 5000 : false;
}


export const createActivityDraftSchema = z.object({
  captainA: z.string().uuid('Выбери капитана A'),
  captainB: z.string().uuid('Выбери капитана B'),
  closeSignup: z.boolean().default(false),
}).refine((data) => data.captainA !== data.captainB, { message: 'Выбери двух разных капитанов', path: ['captainB'] });
export type CreateActivityDraftInput = z.infer<typeof createActivityDraftSchema>;
export interface ActivityDraftContext {
  activityId: string;
  title: string;
  status: string;
  players: DraftPlayerProfile[];
  telegramTitle: string | null;
  room: DraftRoomCreated | null;
}
